import {
  EscalationReason,
  ThreadAIOutcome,
  TriggeredBy,
  TriggerType,
} from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { ThreadAIActivity } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconColorType, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Chat } from '@frontend/design-system';

type Props = {
  aiItem: ThreadAIActivity;
};

const BUBBLE_MAP_KEYS = {
  ACTIVATION: 'activation',
  DEACTIVATION: 'deactivation',
  SCHEDULE_COMPLETE: 'schedule-complete',
  ASSIST_SUCCESS: 'assist-success',
  SCHEDULE_INCOMPLETE: 'schedule-incomplete',
  ASSIST_FAIL: 'assist-fail',
} as const;

type BubbleMapKey = (typeof BUBBLE_MAP_KEYS)[keyof typeof BUBBLE_MAP_KEYS];

export const AiThreadItem = ({ aiItem }: Props) => {
  const { t } = useTranslation('thread-body');
  const timestamp = new Date(aiItem.createdAt || '');

  const bubbleMap: Record<
    BubbleMapKey,
    {
      text: string;
      textColor: string;
      iconName: IconName;
      iconColor: IconColorType;
      bold: string;
      italic: string;
      needsAttention?: boolean;
    }
  > = {
    [BUBBLE_MAP_KEYS.ACTIVATION]: {
      text: t('AI Receptionist is now active'),
      textColor: theme.colors.neutral90,
      iconName: 'x',
      iconColor: 'default',
      bold: 'bold',
      italic: 'normal',
    },
    [BUBBLE_MAP_KEYS.DEACTIVATION]: {
      text: t('AI Receptionist went inactive'),
      textColor: theme.colors.neutral40,
      iconName: 'x',
      iconColor: 'default',
      bold: 'normal',
      italic: 'italic',
    },
    [BUBBLE_MAP_KEYS.SCHEDULE_COMPLETE]: {
      text: t('Patient was added to the schedule'),
      textColor: theme.colors.neutral90,
      iconName: 'calendar',
      iconColor: 'success',
      bold: 'bold',
      italic: 'normal',
    },
    [BUBBLE_MAP_KEYS.ASSIST_SUCCESS]: {
      text: t('Successfully assisted this patient'),
      textColor: theme.colors.neutral90,
      iconName: 'check',
      iconColor: 'success',
      bold: 'bold',
      italic: 'normal',
    },
    [BUBBLE_MAP_KEYS.SCHEDULE_INCOMPLETE]: {
      text: t('Scheduling Incomplete'),
      textColor: theme.colors.neutral90,
      iconName: 'aineeds-attention',
      iconColor: 'default',
      bold: 'bold',
      italic: 'normal',
      needsAttention: true,
    },
    [BUBBLE_MAP_KEYS.ASSIST_FAIL]: {
      text: t('Unable to assist this patient'),
      textColor: theme.colors.neutral90,
      iconName: 'aineeds-attention',
      iconColor: 'default',
      bold: 'bold',
      italic: 'normal',
      needsAttention: true,
    },
  };

  const getBubbleInfo = () => {
    if (
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_ACTIVATION ||
      (aiItem.triggerType === TriggerType.TRIGGER_TYPE_INITIAL_MESSAGE &&
        aiItem.triggeredBy === TriggeredBy.TRIGGERED_BY_PARTNER_AI) ||
      (aiItem.triggerType === TriggerType.TRIGGER_TYPE_INITIAL_MESSAGE &&
        aiItem.triggeredBy === TriggeredBy.TRIGGERED_BY_WEAVE_PATIENT)
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.ACTIVATION];
    }

    if (
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_DEACTIVATION ||
      (aiItem.triggerType === TriggerType.TRIGGER_TYPE_OUTBOUND_MESSAGE &&
        aiItem.triggeredBy === TriggeredBy.TRIGGERED_BY_WEAVE_USER) ||
      (aiItem.triggerType === TriggerType.TRIGGER_TYPE_INITIAL_MESSAGE &&
        aiItem.triggeredBy === TriggeredBy.TRIGGERED_BY_WEAVE_USER)
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.DEACTIVATION];
    }

    if (
      (aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_ASSISTANCE_COMPLETE ||
        aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_APPOINTMENT_CONFIRMATION ||
        aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_CANCELLATION_COMPLETION) &&
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.ASSIST_SUCCESS];
    }

    if (
      (aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_SCHEDULING_COMPLETE ||
        aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_BOOKING_COMPLETION ||
        aiItem.status === ThreadAIOutcome.THREAD_AI_OUTCOME_RESCHEDULING_COMPLETION) &&
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_OUTCOME
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.SCHEDULE_COMPLETE];
    }

    if (
      (aiItem.escalationReason === EscalationReason.ESCALATION_REASON_SERVICE_STAFF_NOT_FOUND ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_BOOKING_DISALLOWED ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_BOOKING_FORWARD ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_RESCHEDULE_FORWARD ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CANCEL_FORWARD ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_NO_AVAILABILITY_FOUND ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_BOOKING_HELP_NEEDED ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_RESCHEDULE_HELP_NEEDED ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CANCEL_HELP_NEEDED ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_BOOKING_FAILURE ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CONFIRMATION_FAILURE) &&
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_ESCALATED
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.SCHEDULE_INCOMPLETE];
    }

    // ADD ASSIST FAIL WHEN API IS READY
    if (
      (aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CALLBACK_CUSTOMER ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_CUSTOMER_UNRESPONSIVE ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_LANDLINE_CALL ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_UNRESPONSIVE_VOICEMAIL ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_RUNNING_LATE ||
        aiItem.escalationReason === EscalationReason.ESCALATION_REASON_BOOKING_NOT_FOUND) &&
      aiItem.triggerType === TriggerType.TRIGGER_TYPE_BOT_ESCALATED
    ) {
      return bubbleMap[BUBBLE_MAP_KEYS.ASSIST_FAIL];
    }

    return;
  };

  return (
    <Chat
      direction='outbound'
      avatar={<Icon name='aiassistant' size={16} color='subdued' />}
      timestamp={timestamp}
      css={{
        padding: theme.spacing(0, 3),
      }}
    >
      <Chat.Bubble
        variant='ai-activity'
        text={getBubbleInfo()?.text}
        leadingIcon={
          getBubbleInfo()?.iconName !== 'x' && (
            <Icon
              name={getBubbleInfo()?.iconName ?? 'check'}
              size={16}
              color={getBubbleInfo()?.iconColor ?? 'success'}
              css={getBubbleInfo()?.needsAttention ? needsAttentionStyles : undefined}
            />
          )
        }
        styles={{
          height: theme.spacing(2),
          borderLeft:
            aiItem.triggerType !== TriggerType.TRIGGER_TYPE_BOT_DEACTIVATION &&
            aiItem.triggerType !== TriggerType.TRIGGER_TYPE_BOT_ACTIVATION &&
            `2px solid ${theme.colors.neutral40}`,
          borderRadius: 0,
          padding: theme.spacing(0, 2, 0, 1),
          backgroundColor: theme.colors.white,
          p: {
            fontWeight: getBubbleInfo()?.bold,
            fontStyle: getBubbleInfo()?.italic,
            color: getBubbleInfo()?.textColor,
          },
        }}
      />
    </Chat>
  );
};

// will move to a shared widget in future pr that is currently WIP
const needsAttentionStyles = {
  borderRadius: theme.borderRadius.small,
  background: `linear-gradient(100deg, ${theme.colors.warning10} 0%, ${theme.colors.warning5} 100%)`,
};
